import { ArrowIcon, Footer, SiteHeader } from '../components/SiteShell'
import { BookGrid } from '../components/BookDiscovery'

const testimonials = [
  ['It looks like a book we would keep forever.', 'Maya, mum of two'],
  ['She spotted herself instantly—and asked to read it again.', 'Clara, mum of one'],
  ['A proper keepsake, without a complicated design process.', 'James, dad of three'],
]

export default function HomePage() {
  return (
    <>
      <SiteHeader />
      <main>
        <section className="hero">
          <div className="hero-doodle hero-doodle-star">✦</div>
          <div className="hero-doodle hero-doodle-loop">⌁</div>
          <div className="shell hero-grid">
            <div className="hero-copy">
              <span className="eyebrow">Personalized books made for one</span>
              <h1>Their little face.<br /><em>Their new</em><br />favorite book.</h1>
              <p>Choose a story they’ll love, then turn one favorite photo into a beautifully illustrated hardcover starring your child.</p>
              <div className="hero-actions">
                <a className="button" href="#books">Shop the books <ArrowIcon /></a>
                <span>Free personalized preview</span>
              </div>
              <div className="trust-row"><span>★★★★★</span> Loved by thoughtful parents</div>
            </div>
            <div className="hero-art" aria-label="A personalized Lucie's Book shown open">
              <div className="sun-shape" />
              <img className="hero-child" src="/images/header-banner.jpg" alt="Child enjoying a personalized art book" />
              <div className="floating-card floating-card-one"><span>made for</span><strong>the wonderfully<br />curious</strong></div>
              <div className="floating-card floating-card-two"><span>01</span><strong>One photo</strong><small>is all you need</small></div>
            </div>
          </div>
        </section>

        <section className="promise-strip">
          <div className="shell promise-grid">
            <p><span>01</span><strong>Made personal</strong>Every illustration stars your child.</p>
            <p><span>02</span><strong>Made beautifully</strong>Rich color, thick pages, keepsake binding.</p>
            <p><span>03</span><strong>Made effortless</strong>One photo. A few choices. Done.</p>
          </div>
        </section>

        <section className="section shell" id="how-it-works">
          <div className="section-heading split-heading">
            <div><span className="eyebrow">Simple by design</span><h2>From camera roll<br />to <em>keepsake.</em></h2></div>
            <p>We know family life is full. Lucie's Book makes something deeply personal without giving you another project to manage.</p>
          </div>
          <div className="steps-grid">
            <article><div className="step-visual coral"><img src="/images/sample-1.png" alt="Clear portrait photo example" /><span>1</span></div><h3>Choose one photo</h3><p>A clear, front-facing photo works best. We’ll guide you along the way.</p></article>
            <article><div className="step-visual yellow"><div className="mini-book"><img src="/images/sample-2.png" alt="Illustrated portrait preview" /></div><span>2</span></div><h3>See the magic</h3><p>Preview your child inside the story and watch the first pages come to life.</p></article>
            <article><div className="step-visual blue"><img className="finished-book-image" src="/images/sample-3-finished.jpg" alt="Child holding their finished personalized book" /><span>3</span></div><h3>Make it theirs</h3><p>Fine-tune the pages, then we print and send their story home.</p></article>
          </div>
        </section>

        <section className="book-shop section" id="books">
          <div className="shell">
            <div className="shop-heading">
              <div><span className="eyebrow">The Lucie's Book collection</span><h2>Find<br /><em>their book.</em></h2></div>
              <p>Choose a story, then pick the edition that feels right: realistic or hand-inked. Each pair follows the same complete book.</p>
            </div>
            <div className="shop-assurance"><span>Free personalized preview</span><span>Premium hardcover</span><span>Free shipping</span></div>
            <BookGrid />
            <div className="collection-more-wrap">
              <a className="button collection-more" href="/books/">See all books <ArrowIcon /></a>
            </div>
          </div>
        </section>

        <section className="love-browse section shell" aria-labelledby="love-browse-title">
          <div className="section-heading">
            <span className="eyebrow">A story that feels like them</span>
            <h2 id="love-browse-title">Browse by what<br /><em>they love.</em></h2>
          </div>
          <div className="love-grid">
            <a className="love-card love-card-learning" href="/books/"><span>Curious little minds</span><img src="/images/theme-my-body.jpg" alt="" /><h3>Learning &amp;<br />Discovery</h3><small>Find their next fascination <ArrowIcon /></small></a>
            <a className="love-card love-card-feelings" href="/books/"><span>Growing with confidence</span><img src="/images/theme-my-emotions.jpg" alt="" /><h3>Feelings &amp;<br />Growing Up</h3><small>Stories for big feelings <ArrowIcon /></small></a>
          </div>
        </section>

        <section className="book-feature" id="inside">
          <div className="shell feature-grid">
            <div className="feature-images">
              <img className="feature-main" src="/images/art-history-banner.jpg" alt="Personalized art history book" />
              <div className="feature-note">Every<br /><small>personalized<br />page</small></div>
            </div>
            <div className="feature-copy">
              <span className="eyebrow">Made to live on the bookshelf</span>
              <h2>A real book.<br /><em>Really about them.</em></h2>
              <p>Every Lucie's Book brings your child into a story created around them. It feels magical to little readers and genuinely beautiful to grown-ups.</p>
              <ul>
                <li><span>✓</span><div><strong>Personalized on every page</strong><small>Their face and name belong naturally inside the story.</small></div></li>
                <li><span>✓</span><div><strong>Premium hardcover</strong><small>20 × 20 cm, designed for repeat reading.</small></div></li>
                <li><span>✓</span><div><strong>A gift with staying power</strong><small>Thoughtful for birthdays, holidays, and “just because.”</small></div></li>
              </ul>
              <a className="text-link" href="#books">Choose their story <ArrowIcon /></a>
            </div>
          </div>
        </section>

        <section className="section shell reviews">
          <div className="section-heading"><span className="eyebrow">From their bookshelves</span><h2>Small readers.<br /><em>Big reactions.</em></h2></div>
          <div className="reviews-grid">{testimonials.map(([quote, name], index) => <blockquote key={name}><span>“</span><p>{quote}</p><footer><i>{index + 1}</i>{name}</footer></blockquote>)}</div>
        </section>

        <section className="final-cta">
          <div className="shell"><span className="eyebrow">Their next favorite story</span><h2>There’s only one<br /><em>just like them.</em></h2><p>Choose the book that feels made for them.</p><a className="button button-light" href="#books">Shop personalized books <ArrowIcon /></a></div>
        </section>
      </main>
      <Footer />
    </>
  )
}
